// @ts-nocheck

import ListingWrapper from 'app/shared/modules/listing-cards/ListingWrapper';

import React from 'react';
import styled from 'styled-components';
import { stacked } from 'app/shared/styles/_spacing';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import cx from 'classnames';
import Head from 'react-helmet';
import last from 'lodash/last';
import map from 'lodash/map';
import PropTypes from 'prop-types';

// Actions
import UserItemActions from 'app/shared/flux/actions/UserItemActions';

// Components
import Row from 'app/shared/core/Row';
import Select from 'app/shared/core/Select';
import Spinner from 'app/shared/modules/Spinner';
import TabGroup from 'app/shared/modules/TabGroup';
import TabItem from 'app/shared/modules/TabItem';

// Misc / Utils
import './styles.scss';
import controller from './controller';
import Pagination from 'app/shared/modules/Pagination';
import pathsManifest from 'app/shared/utils/pathsManifest';
import queryUtils from 'app/shared/utils/queryUtils';
import RoleCheck from 'app/shared/wrappers/RoleCheck';
import { emitTrackSortListingsEvent } from 'app/shared/models/Clickstream/ListingsClickstreamEvents';
import {
  emitTrackViewHiddenListingsPageEvent,
  emitTrackViewViewedListingsPageEvent,
} from 'app/shared/models/Clickstream/PageViewEvents';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';

const logger = getGlobalLogger('pages/userItemsPage');
const PAGE_SIZE = 20;

const StyledTabGroup = styled(TabGroup)`
  ${stacked._4x};
`;

class UserItemsPage extends React.Component {
  static propTypes = {
    userItemType: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      sortBy: 'added',
      loading: true,
      error: false,
      totalItems: 0,
    };
  }

  componentDidMount() {
    const { dispatch, userItemType } = this.props;

    this.getUserItems();

    if (userItemType === 'hidden') {
      emitTrackViewHiddenListingsPageEvent();
    } else {
      emitTrackViewViewedListingsPageEvent();
    }
  }

  componentDidUpdate(prevProps) {
    const prevOffset = parseInt(prevProps.query.start) || 0;
    const currOffset = parseInt(this.props.query.start) || 0;
    if (prevProps.userItemType !== this.props.userItemType || currOffset !== prevOffset) {
      this.getUserItems();
    }
  }

  getUserItems = () => {
    const { query } = this.props;

    // to prevent flash of Next button
    this.setState({ totalItems: 0 });

    const offset = parseInt(query.start, 10) || 0;

    this.getNextUserItems(offset);
  };

  getNextUserItems = (offset) => {
    const { userItemType } = this.props;

    this.setState({
      loading: true,
    });
    window.scrollTo(0, 0);

    this.props
      .dispatch(UserItemActions.getUserItems(userItemType, offset, PAGE_SIZE))
      .then((result) => {
        const totalItems = result.data.totalAvailable;
        this.setState({
          loading: false,
          totalItems,
        });
      })
      .catch((err) => {
        logError({
          error: err,
          context: `UserItemsPage#getNextUserItems Failed to load ${userItemType} items. Server error.`,
        });
        this.setState({
          error: true,
          loading: false,
        });
      });
  };

  handleSortChange = (event) => {
    const { dispatch, userItemType } = this.props;
    const sortBy = event.target.value;
    this.setState({ sortBy });

    emitTrackSortListingsEvent({
      triggerLocation: 'user_item_viewed_page',
      triggerObject: 'user_item_page|header',
    });
  };

  handleViewedTabButton = () => {
    window.router.transitionTo(pathsManifest.ViewedItemsPage);
  };

  handleHiddenTabButton = () => {
    window.router.transitionTo(pathsManifest.HiddenItemsPage);
  };

  renderItems = () => {
    const { currentPathname, listings, userItemType } = this.props;
    const { sortBy } = this.state;
    const sortedListings = controller.getSortedListings({ listings, sortBy });
    let renderItems;

    let triggerLocation = 'user_item_viewed_page';

    if (currentPathname === pathsManifest.HiddenItemsPage) {
      triggerLocation = 'user_item_hidden_page';
    }

    if (sortedListings.length) {
      renderItems = map(sortedListings, (listing) => {
        return (
          <ListingWrapper
            key={userItemType + listing.aliasEncoded}
            listing={listing}
            setActiveMarkerOnHover={false}
            triggerLocation={triggerLocation}
            triggerObject="property_card"
          />
        );
      });
    } else if (this.state.loading) {
      renderItems = <Spinner className="Spinner-container" />;
    } else if (this.state.error) {
      renderItems = <p>There was an error fetching your {userItemType} listings.</p>;
    } else {
      renderItems = <p>You have no {userItemType} listings.</p>;
    }

    return renderItems;
  };

  renderTab = () => {
    const { userItemType } = this.props;
    const { sortBy } = this.state;

    return (
      <>
        {userItemType === 'hidden' && (
          <div className="UserItemsPage-hidden-blurb">
            Listings that are hidden will no longer show up in your searches or on the map.
          </div>
        )}
        <div
          className={cx('UserItemsPage-sort-dropdown', {
            'UserItemsPage-sort-dropdown-md': sortBy === 'added' && userItemType !== 'contacted',
            'UserItemsPage-sort-dropdown-lg': sortBy === 'added' && userItemType === 'contacted',
          })}
        >
          <Select value={sortBy} onChange={this.handleSortChange} aria-label="sort by">
            <option value="added">Sort: Date {userItemType} (recent first)</option>
            <option value="lowPrice">Sort: Lowest price</option>
            <option value="highPrice">Sort: Highest price</option>
            <option value="updated">Sort: Date updated</option>
          </Select>
        </div>
        {this.renderItems()}
        <Pagination
          maxItemsPerPage={PAGE_SIZE}
          totalItems={this.state.totalItems}
          onClick={(offset) => this.getNextUserItems(offset)}
          {...this.props}
        />
      </>
    );
  };

  render() {
    const { currentPathname, userItemType } = this.props;

    return (
      <div className="UserItemsPage">
        <Head title={`${capitalize(userItemType)} listings | HotPads`} />
        <Row size="md">
          <StyledTabGroup appearance="button" maxWidth="380px">
            <TabItem
              active={currentPathname === pathsManifest.ViewedItemsPage}
              tabTitle="Viewed"
              onClick={this.handleViewedTabButton}
            >
              {this.renderTab()}
            </TabItem>
            <TabItem
              active={currentPathname === pathsManifest.HiddenItemsPage}
              tabTitle="Hidden"
              onClick={this.handleHiddenTabButton}
            >
              {this.renderTab()}
            </TabItem>
          </StyledTabGroup>
        </Row>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const userItemType = last(ownProps.location.pathname.split('/'));

  return {
    currentPathname: state.location.current.pathname,
    listings: state.listings.listingGroups[userItemType],
    query: queryUtils.parse(ownProps.location.search),
    userItemType,
  };
};

export default withRouter(
  connect(mapStateToProps, null, null, { pure: false })(
    RoleCheck({ roleReq: 'user', verifiedReq: false })(UserItemsPage),
  ),
);
